import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type SyntheticEvent } from 'react';

import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Drawer } from '../../components/Drawer';
import { Field, inputClass } from '../../components/Field';
import { FormProblem } from '../../components/FormProblem';
import { useToast } from '../../components/Toast';
import { ApiError, NetworkError } from '../../lib/api';
import { useAuth } from '../auth/AuthProvider';
import {
  createRole,
  deleteRole,
  fieldMessages,
  kindLabels,
  rolesKey,
  updateRole,
  type Role,
} from './api';

interface Problem {
  fields: Record<string, string>;
  form: string | null;
}

const noProblem: Problem = { fields: {}, form: null };

function toProblem(err: unknown): Problem {
  if (err instanceof ApiError) {
    if (err.status === 422) return { fields: fieldMessages(err.fields), form: null };
    if (err.status === 409 && err.code === 'role_in_use')
      return { fields: {}, form: 'Mude o perfil dos usuários que o usam antes de excluir.' };
    if (err.status === 409 && err.code === 'role_builtin')
      return { fields: {}, form: 'Os perfis padrão não podem ser excluídos.' };
    if (err.status === 403) return { fields: {}, form: 'Você não tem permissão para esta ação.' };
    if (err.status === 404) return { fields: {}, form: 'Este perfil não existe mais.' };
  }
  if (err instanceof NetworkError)
    return { fields: {}, form: 'Não foi possível contatar o servidor. Tente novamente.' };
  return { fields: {}, form: 'Ocorreu um erro inesperado. Tente novamente.' };
}

const kindHelp: Record<Role['kind'], string> = {
  org: 'Equipe da empresa.',
  client: 'Pessoas do cliente: veem todas as contas do seu cliente.',
  account: 'Pessoas do cliente: veem só as contas liberadas para elas.',
  integration: 'Outros sistemas que recebem o vídeo; não é dado a pessoas.',
};

interface Props {
  /** Undefined opens the drawer to create a profile. */
  role?: Role;
  roles: Role[];
  canManage: boolean;
  onClose: () => void;
}

/** Name and description of a profile; its permissions are in the table (ADR-027). */
export function ProfileDrawer({ role, roles, canManage, onClose }: Props) {
  const { request } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const sources = roles.filter((r) => r.kind !== 'integration');
  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [from, setFrom] = useState(sources.find((r) => r.key === 'operator')?.id ?? '');
  const [problem, setProblem] = useState<Problem>(noProblem);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const editable = canManage && !role?.builtin;
  const fromKind = sources.find((r) => r.id === from)?.kind;

  const save = useMutation({
    mutationFn: () =>
      role
        ? updateRole(request, role.id, { name, description })
        : createRole(request, { name, description, from }),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: rolesKey });
      notify(
        role
          ? `Perfil ${r.name} atualizado.`
          : `Perfil ${r.name} criado. Ajuste as permissões na tabela.`,
      );
      onClose();
    },
    onError: (err) => {
      setProblem(toProblem(err));
    },
  });

  const remove = useMutation({
    mutationFn: () => deleteRole(request, role?.id ?? ''),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: rolesKey });
      notify(`Perfil ${role?.name ?? ''} excluído.`);
      onClose();
    },
    onError: (err) => {
      setConfirmDelete(false);
      setProblem(toProblem(err));
    },
  });

  const submit = (e: SyntheticEvent) => {
    e.preventDefault();
    setProblem(noProblem);
    save.mutate();
  };

  return (
    <Drawer
      title={role ? role.name : 'Novo perfil'}
      description={
        role
          ? `${kindLabels[role.kind]} · ${String(role.users)} ${role.users === 1 ? 'usuário' : 'usuários'}`
          : 'O novo perfil começa com as permissões do perfil escolhido.'
      }
      onClose={onClose}
      footer={
        editable ? (
          <>
            {role && (
              <Button
                className="mr-auto text-crit"
                variant="ghost"
                disabled={role.users > 0}
                title={role.users > 0 ? 'Mude o perfil dos usuários que o usam antes.' : undefined}
                onClick={() => {
                  setConfirmDelete(true);
                }}
              >
                Excluir
              </Button>
            )}
            <Button onClick={onClose}>Cancelar</Button>
            <Button variant="primary" type="submit" form="profile" disabled={save.isPending}>
              {save.isPending ? 'Salvando…' : role ? 'Salvar' : 'Criar perfil'}
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>Fechar</Button>
        )
      }
    >
      <form id="profile" onSubmit={submit} noValidate className="space-y-4">
        <FormProblem message={problem.form} />
        {role?.builtin && (
          <p className="rounded-md border border-line bg-canvas px-3 py-2 text-xs text-fg-muted">
            {role.locked
              ? 'Perfil padrão. O administrador geral sempre tem todas as permissões.'
              : 'Perfil padrão: o nome não muda e ele não pode ser excluído; as permissões são ajustadas na tabela.'}
          </p>
        )}
        <Field label="Nome" error={problem.fields.name}>
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              data-autofocus
              value={name}
              readOnly={!editable}
              maxLength={60}
              onChange={(e) => {
                setName(e.target.value);
              }}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              className={`${inputClass} ${editable ? '' : 'text-fg-muted'}`}
            />
          )}
        </Field>
        <Field label="Descrição" error={problem.fields.description}>
          {({ id, describedBy, invalid }) => (
            <textarea
              id={id}
              value={description}
              readOnly={!editable}
              maxLength={300}
              rows={3}
              onChange={(e) => {
                setDescription(e.target.value);
              }}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              className={`${inputClass} h-auto py-2 ${editable ? '' : 'text-fg-muted'}`}
            />
          )}
        </Field>
        {role ? (
          <p className="text-xs text-fg-subtle">{kindHelp[role.kind]}</p>
        ) : (
          <Field
            label="Começar com as permissões de"
            {...(fromKind ? { hint: `Tipo: ${kindHelp[fromKind]}` } : {})}
            error={problem.fields.from}
          >
            {({ id, describedBy, invalid }) => (
              <select
                id={id}
                value={from}
                onChange={(e) => {
                  setFrom(e.target.value);
                }}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                className={inputClass}
              >
                <option value="" disabled>
                  Escolha um perfil
                </option>
                {sources.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            )}
          </Field>
        )}
      </form>
      {confirmDelete && role && (
        <ConfirmDialog
          title={`Excluir o perfil ${role.name}?`}
          confirmLabel="Excluir perfil"
          busy={remove.isPending}
          onConfirm={() => {
            remove.mutate();
          }}
          onCancel={() => {
            setConfirmDelete(false);
          }}
        >
          O perfil deixa de existir. Esta ação não pode ser desfeita.
        </ConfirmDialog>
      )}
    </Drawer>
  );
}
